import { CONFIG, VaultMeta } from '../config'
import { toBase64 } from './crypto'

class ApiClient {
  private baseUrl: string

  constructor() {
    this.baseUrl = CONFIG.API_BASE
  }

  private async request<T>(
    path: string,
    options: RequestInit = {}
  ): Promise<T> {
    const response = await fetch(`${this.baseUrl}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      credentials: 'include', // Include CF Access cookies
    })

    if (!response.ok) {
      const error = (await response
        .json()
        .catch(() => ({ message: 'Request failed' }))) as { message?: string }
      throw new Error(error?.message || `HTTP ${response.status}`)
    }

    return response.json()
  }

  // Get current user info (email from CF Access)
  async getCurrentUser(): Promise<{ email: string }> {
    return this.request('/user')
  }

  // Create a new vault
  async createVault(data: {
    id: string
    authType: 'passphrase' | 'passkey'
    salt: string
    verificationTag: string
    credentialId?: string
    publicKey?: string
  }): Promise<{ success: boolean }> {
    return this.request('/vaults', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  // Get vault metadata (salt, verification tag, etc.)
  async getVaultMeta(vaultId: string): Promise<VaultMeta | null> {
    try {
      return await this.request(`/vaults/${vaultId}/meta`)
    } catch {
      return null
    }
  }

  // Download encrypted vault data
  async downloadVaultData(
    vaultId: string,
    verificationTag: Uint8Array
  ): Promise<Uint8Array> {
    const response = await fetch(`${this.baseUrl}/vaults/${vaultId}/data`, {
      headers: {
        'X-Vault-Verification': toBase64(verificationTag),
      },
      credentials: 'include',
    })

    if (!response.ok) {
      throw new Error('Failed to download vault data')
    }

    const buffer = await response.arrayBuffer()
    return new Uint8Array(buffer)
  }

  // Upload encrypted vault data
  async uploadVaultData(
    vaultId: string,
    verificationTag: Uint8Array,
    data: Uint8Array
  ): Promise<{ success: boolean }> {
    const response = await fetch(`${this.baseUrl}/vaults/${vaultId}/data`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/octet-stream',
        'X-Vault-Verification': toBase64(verificationTag),
      },
      body: data,
      credentials: 'include',
    })

    if (!response.ok) {
      throw new Error('Failed to upload vault data')
    }

    return response.json()
  }

  // Delete a vault
  async deleteVault(
    vaultId: string,
    verificationTag: Uint8Array
  ): Promise<{ success: boolean }> {
    return this.request(`/vaults/${vaultId}`, {
      method: 'DELETE',
      headers: {
        'X-Vault-Verification': toBase64(verificationTag),
      },
    })
  }

  // Get user's bookmarked vaults
  async getBookmarks(): Promise<string[]> {
    const result = await this.request<{ vaults: string[] }>('/user/bookmarks')
    return result.vaults
  }

  // Add vault to bookmarks
  async addBookmark(vaultId: string): Promise<{ success: boolean }> {
    return this.request('/user/bookmarks', {
      method: 'POST',
      body: JSON.stringify({ vaultId }),
    })
  }

  // Remove vault from bookmarks
  async removeBookmark(vaultId: string): Promise<{ success: boolean }> {
    return this.request(`/user/bookmarks/${vaultId}`, {
      method: 'DELETE',
    })
  }
}

export const api = new ApiClient()
